import React, { useEffect, useState } from 'react'
import Layout from '../components/Layout'
import Table from '../components/common/Table'
import Button from '../components/common/Button'
import ContractTabs from '../components/ContractTabs'
import ContractPaymentCreate from '../components/ContractPaymentCreate'
import { useModal } from '../hooks/modal'
import { useLoad, usePostRequest } from '../hooks/request'
import { BILLING_OPENING } from '../urls'
import { checkPermission as cp } from '../utils/auth'
import { PermissionDenied } from '../components/PermissionDenied'

function money(value) {
    return new Intl.NumberFormat('ru-RU').format(value || 0)
}

function monthOf(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/** O'tgan oy — yig'ma qarz sukut bo'yicha shu oyga bog'lanadi. */
function previousMonth() {
    const now = new Date()
    return monthOf(new Date(now.getFullYear(), now.getMonth() - 1, 1))
}

function rowKey(row) {
    return `${row.module}-${row.companyId}`
}

/**
 * O'tgan davrlar (tizimga o'tishdan oldingi) qarzi.
 *
 * Har bir oyni ochib chiqish o'rniga korxonaning butun eski qarzi bitta
 * yig'ma qator sifatida kiritiladi. Bu qator oylik ro'yxat va KPI ga
 * kirmaydi, lekin "Общий долг" da hisobga olinadi va to'lov qabul qiladi.
 */
export default function ContractOpening() {
    const [period, setPeriod] = useState(previousMonth())
    const [module, setModule] = useState('')
    const [search, setSearch] = useState('')
    const [edited, setEdited] = useState({})
    const [notice, setNotice] = useState(null)

    const params = { period, ...(module ? { module } : {}), ...(search ? { search } : {}) }
    const rows = useLoad({ url: BILLING_OPENING, params }, [period, module, search])
    const save = usePostRequest({ url: BILLING_OPENING })

    // Filtr yoki oy o'zgarsa — saqlanmagan tahrirlar bekor qilinadi.
    useEffect(() => setEdited({}), [period, module, search])

    const response = rows.response || {}
    const items = response.results || []
    const totals = response.totals || {}

    function valueOf(row) {
        const change = edited[rowKey(row)]
        return change === undefined ? String(row.amount || '') : change
    }

    function onChange(row, value) {
        setEdited({ ...edited, [rowKey(row)]: value.replace(/[^0-9]/g, '') })
    }

    async function onSave() {
        const changed = items.filter((row) => {
            const change = edited[rowKey(row)]
            return change !== undefined && Number(change || 0) !== (row.amount || 0)
        })

        if (!changed.length) {
            setNotice({ type: 'is-warning', text: 'Изменений нет' })
            return
        }

        const { success, response: result } = await save.request({
            data: {
                period,
                items: changed.map((row) => ({
                    module: row.module,
                    company_id: row.companyId,
                    amount: Number(edited[rowKey(row)] || 0),
                })),
            },
        })

        if (!success) {
            setNotice({ type: 'is-danger', text: 'Не удалось сохранить' })
            return
        }

        const parts = []
        if (result.created) parts.push(`добавлено ${result.created}`)
        if (result.updated) parts.push(`изменено ${result.updated}`)
        if (result.deleted) parts.push(`удалено ${result.deleted}`)
        if (result.skipped) parts.push(`пропущено ${result.skipped} (есть оплата)`)
        setNotice({ type: 'is-success', text: parts.join(', ') || 'Сохранено' })

        setEdited({})
        rows.request()
    }

    if (!cp('billing.view_contractinvoice')) {
        return <PermissionDenied />
    }

    const dirtyCount = Object.keys(edited).length

    return (
        <Layout>
            <span className="is-size-4">Долг за прошлые периоды</span>
            <br /><br />
            <ContractTabs />

            <div className="notification is-info is-light">
                Здесь вводится <b>общая сумма долга до выбранного месяца</b> — одной строкой,
                без открытия каждого месяца. Строка не попадает в помесячный список и его KPI,
                но учитывается в «Общий долг компаний» и принимает оплату.
                С месяца после выбранного долг ведётся как обычно.
            </div>

            {notice ? (
                <div className={`notification ${notice.type} is-light`}>
                    <button type="button" className="delete" onClick={() => setNotice(null)} />
                    {notice.text}
                </div>
            ) : null}

            <div className="columns">
                <div className="column">
                    <div className="box">
                        <p className="heading">Долг за прошлые периоды</p>
                        <p className="title is-5">{money(totals.amountTotal)}</p>
                    </div>
                </div>
                <div className="column">
                    <div className="box">
                        <p className="heading">Оплачено</p>
                        <p className="title is-5 has-text-success">{money(totals.paidTotal)}</p>
                    </div>
                </div>
                <div className="column">
                    <div className="box">
                        <p className="heading">Остаток</p>
                        <p className="title is-5 has-text-danger">{money(totals.debtTotal)}</p>
                    </div>
                </div>
                <div className="column">
                    <div className="box">
                        <p className="heading">Компаний с долгом</p>
                        <p className="title is-5">{totals.companyCount || 0}</p>
                    </div>
                </div>
            </div>

            <div className="columns">
                <div className="column is-2">
                    <input
                        className="input"
                        type="month"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)} />
                </div>
                <div className="column is-2">
                    <div className="select is-fullwidth">
                        <select value={module} onChange={(e) => setModule(e.target.value)}>
                            <option value="">Все модули</option>
                            <option value="zero">Nollik</option>
                            <option value="manthly">Oylik</option>
                        </select>
                    </div>
                </div>
                <div className="column">
                    <input
                        className="input"
                        placeholder="Поиск по названию или СТИР..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="column is-narrow">
                    <Button
                        onClick={onSave}
                        loading={save.loading}
                        text={dirtyCount ? `Сохранить (${dirtyCount})` : 'Сохранить'}
                        icon="ion-md-checkmark"
                        className="is-success" />
                </div>
            </div>

            <Table
                loading={rows.loading}
                items={items}
                columns={{
                    company: 'Компания',
                    contract: 'Договор',
                    amount: 'Долг за прошлые периоды',
                    paid: 'Оплачено',
                    debt: 'Остаток',
                    actions: '',
                }}
                renderItem={(row) => (
                    <OpeningRow
                        key={rowKey(row)}
                        row={row}
                        value={valueOf(row)}
                        onChange={(value) => onChange(row, value)}
                        onPaid={() => rows.request()} />
                )} />
        </Layout>
    )
}

function OpeningRow({ row, value, onChange, onPaid }) {
    const [showPaymentModal, hidePaymentModal] = useModal(
        <ContractPaymentCreate
            invoice={{
                id: row.invoiceId,
                companyName: row.companyName,
                expectedAmount: row.amount,
                paid: row.paid,
                debt: row.debt,
            }}
            onSuccess={() => {
                hidePaymentModal()
                onPaid()
            }}
            onCancel={() => hidePaymentModal()} />,
    )

    return (
        <tr>
            <td>
                {row.companyName}
                <br />
                <span className="has-text-grey is-size-7">
                    {row.module === 'zero' ? 'Nollik' : 'Oylik'}{row.stir ? ` · ${row.stir}` : ''}
                </span>
            </td>
            <td className="has-text-grey">{money(row.contractSum)}</td>
            <td>
                <input
                    className="input is-small"
                    style={{ minWidth: '9rem' }}
                    inputMode="numeric"
                    value={value}
                    placeholder="0"
                    onChange={(e) => onChange(e.target.value)} />
            </td>
            <td className="has-text-success">{money(row.paid)}</td>
            <td className={row.debt > 0 ? 'has-text-danger' : 'has-text-grey'}>{money(row.debt)}</td>
            <td className="has-text-right">
                {row.invoiceId && row.debt > 0 ? (
                    <Button
                        onClick={showPaymentModal}
                        text="Оплата"
                        icon="ion-md-add"
                        className="is-link is-outlined is-small" />
                ) : null}
            </td>
        </tr>
    )
}
